<template>
    <ModalWrapper v-model="modal" max-width="650" :loading="loading">
        <template v-if="name || hotelInfo?.hotelName" v-slot:title>
            {{ name || hotelInfo?.hotelName }}
            <v-rating
                v-if="stars"
                active-color="amber"
                color="grey-lighten-2"
                empty-icon="mdi-star"
                size="small"
                density="compact"
                readonly
                :model-value="parseInt(stars)"
            />
        </template>
        <div v-if="image" class="pb-2">
            <v-img
                tile
                class="order-info-base__img thumb"
                :src="$filters.imageUrl(image, 200)"
                :lazy-src="$filters.srcHost('/placeholder.png')"
                min-height="100"
                :eager="true"
                :max-height="$breakpoint.smAndDown ? 200 : 200"
            />
            <slot name="belowImg" />
        </div>
        <template v-if="hotelInfo">
            <div v-if="hotelInfo?.address?.addressLine" class="d-flex align-center">
                <v-icon start size="small" icon="mdi-map-marker-outline" />
                <small>{{ hotelInfo?.address?.addressLine }}</small>
            </div>
            <div
                v-if="hotelInfo?.email && useCustomizationHelpers().isShowHotelEmailOnInfo"
                class="d-flex align-center text-caption"
            >
                <v-icon start size="small" icon="mdi-at" />
                {{ hotelInfo?.email }}
            </div>
            <div v-if="hotelInfo?.url" class="d-flex align-center text-caption">
                <v-icon start size="small" icon="mdi-web" />
                <a :href="hotelInfo.url" target="_blank" class="text-caption">
                    {{ hotelInfo.url }}
                </a>
            </div>
            <v-row v-if="hotelInfo?.phone" no-gutters dense>
                <v-col :cols="12" md="12">
                    <div class="d-flex align-center text-caption">
                        <v-icon start size="small" icon="mdi-phone" />
                        {{ hotelInfo.phone }}
                    </div>
                </v-col>
            </v-row>
            <v-row v-if="hotelInfo?.fax" no-gutters dense>
                <v-col :cols="12" md="12">
                    <div class="d-flex align-center text-caption">
                        <v-icon start size="small" icon="mdi-fax" />
                        {{ hotelInfo.fax }}
                    </div>
                </v-col>
            </v-row>
            <v-row v-if="hotelInfo?.descriptions?.description?.length" dense>
                <v-col :cols="12" md="12" class="mt-5 pl-8">
                    <div v-if="hotelInfo.descriptions.description.find(el => el.type === 'short')" class="mt-2">
                        <span class="d-block text-subtitle-1 font-weight-medium">
                            {{ $t('description_type.short') }}
                        </span>
                        <span v-html="hotelInfo.descriptions.description.find(el => el.type === 'short').description" />
                    </div>
                    <div v-if="hotelInfo.descriptions.description.find(el => el.type === 'general')" class="mt-2">
                        <span class="d-block text-subtitle-1 font-weight-medium">
                            {{ $t('description_type.general') }}
                        </span>
                        <span
                            v-html="hotelInfo.descriptions.description.find(el => el.type === 'general').description"
                        />
                    </div>
                    <div v-if="hotelInfo.descriptions.description.find(el => el.type === 'pleasenote')" class="mt-2">
                        <span class="d-block text-subtitle-1 font-weight-medium">
                            {{ $t('important_information') }}
                        </span>
                        <span
                            v-html="hotelInfo.descriptions.description.find(el => el.type === 'pleasenote').description"
                        />
                    </div>
                </v-col>
            </v-row>
            <v-row v-if="hotelInfo.services.service.length" dense>
                <v-col cols="12" class="pt-6">
                    <v-row no-gutters>
                        <v-col cols="12" sm="3" class="col-title">
                            <v-icon start size="small" icon="mdi-star-circle-outline" />
                            {{ $t('services') }}:
                        </v-col>
                        <v-col class="pl-2 d-flex flex-wrap">
                            <v-chip
                                v-for="service in hotelInfo.services.service"
                                :key="service.supplierSpecificCode"
                                size="small"
                                class="px-2 mr-1 mt-1"
                            >
                                {{ service.otaname || service.supplierServiceName }}
                            </v-chip>
                        </v-col>
                    </v-row>
                </v-col>
            </v-row>
        </template>
    </ModalWrapper>
</template>
<script setup lang="ts">
    import {EventBus, SHOW_HOTEL_INFO_MODAL} from '@/utils/event-bus'

    const hotelInfo = ref(null)
    const name = ref(null)
    const stars = ref(null)
    const image = ref(null)
    const loading = ref<boolean>(false)
    const modal = ref<boolean>(false)

    const {$api, $toast} = useNuxtApp()

    onMounted(async () => {
        EventBus.$on(SHOW_HOTEL_INFO_MODAL, loadHotelInfo)
    })

    watch(
        () => modal.value,
        async (): Promise<void> => {
            if (modal.value) {
                hotelInfo.value = null
            }
        }
    )

    onBeforeUnmount(() => {
        EventBus.$off(SHOW_HOTEL_INFO_MODAL, loadHotelInfo)
        hotelInfo.value = null
    })

    const loadHotelInfo = async ({hotelId, hotelName, hotelStars, mainImage, request}) => {
        name.value = hotelName
        stars.value = hotelStars
        image.value = mainImage
        modal.value = true

        const rq = hotelId ? {hotelId} : request
        try {
            loading.value = true
            const {hotelDescriptionInfo} = await $api.hotelInfo.get(rq)
            hotelInfo.value = hotelDescriptionInfo
            image.value = hotelInfo.value?.mainImage?.url
        } catch (e) {
            hotelInfo.value = null
            $toast.error(e.msg || e.errors[0].message)
        } finally {
            loading.value = false
        }
    }
</script>
